import { Badge, Box, Button, Card, CheckIcon, Code, type ComboboxLikeRenderOptionInput, Group, Select, Skeleton, Stack, Table, Text, Title, Tooltip } from "@mantine/core";
import { useReducedMotion } from "@mantine/hooks";
import { type ReactNode, useLayoutEffect, useMemo, useReducer, useRef, useState } from "react";
import { FactTable } from "../../common/fact-table";
import { InfoMark } from "../../common/info-mark";
import { useInitialHashState, useRegisterShareState } from "../../common/share-state";
import { UtilityTitle } from "../../common/utility-title";
import { areaText, callingCodes, coordinates, currencyRows, decimalDegrees, demonymRows, isoCodes, languageRows, nativeNameRows } from "./facts";
import { borderCountries, type Country, COUNTRY_OPTIONS, countryFilter, findCountry, pickCountry, VIEW_OPTIONS } from "./list";
import { flight, type Framing, mapOf, type Place, prepare, VIEW_BOX } from "./map";
import { type Boundaries, DEFAULT_VIEW, pickView, useBoundaries } from "./shapes";

export default function Countries() {
  const initialState = useInitialHashState<{ country?: string; view?: string }>();

  const [country, setCountry] = useState(() => pickCountry(initialState?.country));
  const [view, setView] = useState(() => pickView(initialState?.view));

  // Both always sent, and neither left to the reader's own browser: a link with no country in it would open on
  // wherever the person following it happens to be rather than on the country the person sharing it was looking at,
  // and a link with no point of view in it would redraw that country's borders for them.
  useRegisterShareState(() => ({ country: country.cca2, view }));

  const borders = borderCountries(country);
  const prefixes = callingCodes(country);
  const currencies = currencyRows(country);
  const natives = nativeNameRows(country);

  return (
    <Stack gap="md">
      <UtilityTitle directory="countries" publications="Natural Earth">Countries</UtilityTitle>

      <Card withBorder shadow="sm" radius="md">
        <Select
          className="country-picker"
          label="Country"
          description="Searched by name, native name, translation, code, capital or domain"
          data={COUNTRY_OPTIONS}
          value={country.cca2}
          onChange={(code) => setCountry(pickCountry(code))}
          filter={countryFilter}
          leftSection={<Text span>{country.flag}</Text>}
          renderOption={renderCountryOption}
          allowDeselect={false}
          searchable
          // Highlights the best match as the search changes, which also scrolls the list back to it: the dropdown
          // opens scrolled to the selected country, and filtering alone leaves it stranded down among the rest.
          selectFirstOptionOnChange
          nothingFoundMessage="No country by that name"
        />
      </Card>

      <Card withBorder shadow="sm" radius="md" data-country={country.cca2}>
        <Stack gap="sm">
          <Group gap="md" align="center" wrap="nowrap">
            {/* The country's own mark, read as a picture rather than as the text beside it, so set at that size. */}
            <Text span className="country-flag">{country.flag}</Text>
            <Box miw={0}>
              <Title order={3}>{country.name.common}</Title>
              <Text size="sm" c="dimmed">{country.name.official}</Text>
            </Box>
          </Group>
          <Group gap="xs">
            <Marker>{country.region}</Marker>
            {country.subregion && <Marker>{country.subregion}</Marker>}
            <Marker>{country.unMember ? "UN member" : "Not a UN member"}</Marker>
            <Marker>{country.independent ? "Independent" : "Dependent territory"}</Marker>
            {country.landlocked && <Marker>Landlocked</Marker>}
            {/* Kosovo alone: a code somebody handed out rather than one ISO assigned, which is worth saying. */}
            {country.status !== "officially-assigned" && <Marker>Code is {country.status.replace("-", " ")}</Marker>}
          </Group>
        </Stack>
      </Card>

      {/* Two short cards abreast while the region is wide enough for both, and stacked once it is not. */}
      <Box className="card-columns">
        <Card withBorder shadow="sm" radius="md">
          <Stack gap="sm">
            <Title order={4}>Codes</Title>
            <FactTable
              rows={[
                ...isoCodes(country),
                { label: "Olympic (IOC)", value: country.cioc },
                { label: "Internet domain", value: country.tld.join(", ") },
                // The North American plan has three hundred of them, which is a list and not a country's own number.
                { label: "Calling code", value: inlinePrefixes(prefixes) || country.idd.root },
              ]}
            />
            {prefixes.length > INLINE_PREFIXES && (
              <Box>
                <Text size="sm" c="dimmed" mb={6}>{prefixes.length} dialling prefixes</Text>
                {/* Scrolled rather than folded: a fold set in pixels cuts through whichever row of codes it lands on. */}
                <Group gap={4} className="country-prefixes">
                  {prefixes.map((prefix) => <Code key={prefix}>{prefix}</Code>)}
                </Group>
              </Box>
            )}
          </Stack>
        </Card>

        <Card withBorder shadow="sm" radius="md">
          <Stack gap="sm">
            <Title order={4}>Place</Title>
            <FactTable
              rows={[
                { label: "Capital", value: country.capital.join(", ") },
                { label: "Region", value: country.region },
                { label: "Subregion", value: country.subregion },
                { label: "UN regional group", value: country.unRegionalGroup },
                { label: "Area", value: areaText(country) },
                { label: "Coordinates", value: coordinates(country) },
                { label: "Decimal degrees", value: decimalDegrees(country) },
              ]}
            />
            <Box>
              <Text size="sm" c="dimmed" mb={6}>Land borders</Text>
              {borders.length === 0
                ? <Text size="sm">None</Text>
                : (
                  <Group gap="xs">
                    {/* Every neighbour is a country this page can show, so each one is the way to the next. */}
                    {borders.map((border) => (
                      <Button
                        key={border.cca2}
                        variant="default"
                        size="compact-sm"
                        onClick={() => setCountry(border)}
                      >
                        {border.flag} {border.name.common}
                      </Button>
                    ))}
                  </Group>
                )}
            </Box>
          </Stack>
        </Card>
      </Box>

      <Card withBorder shadow="sm" radius="md">
        <Stack gap="sm">
          <Group justify="space-between" align="flex-end">
            <Title order={4}>Map</Title>
            {/* With the card it redraws rather than beside the picker, which is about which country is showing. */}
            <Group className="country-view-row" gap={4} align="flex-end" wrap="nowrap">
              {
                /* Before the picker and not after it: the row ends where the picker does, so a mark that comes and
                  goes with the option it explains moves nothing when it does. */
              }
              {view === DEFAULT_VIEW && <InfoMark label={DEFAULT_MEANS} />}
              <Select
                className="country-view"
                label="Point of view"
                data={VIEW_OPTIONS}
                value={view}
                onChange={(picked) => setView(pickView(picked))}
                renderOption={renderCountryOption}
                allowDeselect={false}
                comboboxProps={{ withinPortal: true }}
              />
            </Group>
          </Group>
          <CountryMap country={country} view={view} onSelect={setCountry} />
        </Stack>
      </Card>

      <Card withBorder shadow="sm" radius="md">
        <Stack gap="lg">
          <Section title="Currencies" empty={currencies.length === 0 && "None of its own"}>
            <ColumnTable
              head={["Code", "Name", "Symbol"]}
              rows={currencies.map((currency) => [currency.code, currency.name, currency.symbol])}
            />
          </Section>
          <Section title="Languages">
            <ColumnTable
              head={["Code", "Language"]}
              rows={languageRows(country).map((language) => [language.code, language.name])}
            />
          </Section>
        </Stack>
      </Card>

      <Card withBorder shadow="sm" radius="md">
        <Stack gap="lg">
          <Section title="Native names" empty={natives.length === 0 && "None recorded"}>
            <ColumnTable
              head={["Language", "Common", "Official"]}
              rows={natives.map((name) => [name.language, name.common, name.official])}
            />
          </Section>
          <Section title="Demonyms">
            <ColumnTable
              head={["Language", "Masculine", "Feminine"]}
              rows={demonymRows(country).map((demonym) => [demonym.language, demonym.masculine, demonym.feminine])}
            />
          </Section>
          <Section title="Alternative spellings">
            <Group gap={4}>{country.altSpellings.map((spelling) => <Code key={spelling}>{spelling}</Code>)}</Group>
          </Section>
        </Stack>
      </Card>
    </Stack>
  );
}

// The country and whatever it borders, framed on the country and drawn from the boundaries the reader's own point of
// view has. Every country the frame holds is one this page can show, so each one is the way to it — the same offer
// the buttons under Place make and rather more of it, which is why none of these is a tab stop of its own: a picture
// repeating a list of links is a second copy of them to tab through and no second way to reach anything.
function CountryMap({ country, view, onSelect }: CountryMapProps) {
  const boundaries = useBoundaries(view);
  const [hovered, setHovered] = useState<string>();
  const still = useReducedMotion();
  const flying = useRef<SVGGElement>(null);
  const framing = useRef<Framing>(undefined);
  // Bumped as a flight lands, which is what redraws the map over its own frame alone: what it was flown across
  // reaches back to where it started, and that is half a continent nobody is looking at once it has arrived.
  const [landing, land] = useReducer((count: number) => count + 1, 0);

  // Measured once when the boundaries arrive rather than on every country picked, which is what makes a click
  // through the map a comparison per country and coordinates walked only for the few in the frame.
  const prepared = useMemo(() => {
    if (!boundaries) return undefined;
    // The two are the same object for a reader with no point of view of their own, which is most of them.
    const world = prepare(boundaries.world);
    return { world, shapes: boundaries.shapes === boundaries.world ? world : prepare(boundaries.shapes) };
  }, [boundaries]);
  const drawn = useMemo(
    () =>
      prepared
      && mapOf(
        prepared.shapes,
        prepared.world,
        country.cca2,
        borderCountries(country).map((each) => each.cca2),
        placeOf(country),
        still ? undefined : framing.current,
      ),
    // Where the last map was framed is read rather than watched, a map being flown from wherever the one it replaces
    // was drawn; `landing` is the other half of that and is what takes the flight back out once it is over.
    [prepared, country, still, landing],
  );

  // Written before the animation is made as well as inside it, so that the frame a flight opens on is the frame it
  // is first painted on: an effect that only starts an animation has already let the destination be drawn once.
  useLayoutEffect(() => {
    framing.current = drawn?.framing;
    const group = flying.current;
    if (!group || !drawn?.from) return;

    const frames = flight(drawn.from, Number.parseFloat(getComputedStyle(group).strokeWidth) || 0);
    group.style.transform = String(frames[0].transform);
    group.style.strokeWidth = String(frames[0].strokeWidth);

    const moving = group.animate(frames, { duration: FLIGHT, easing: EASING, fill: "forwards" });
    const settle = () => {
      group.style.transform = "";
      group.style.strokeWidth = "";
      moving.cancel();
    };
    // Cancelling is what rejects it, and a flight called off by the next one is nothing to report.
    moving.finished.then(() => {
      settle();
      land();
    }, () => {});

    return settle;
  }, [drawn]);

  if (boundaries === undefined) return <Skeleton className="country-map" radius="sm" />;
  if (boundaries === null) return <Text size="sm" c="dimmed">The boundaries could not be read.</Text>;

  const under = hovered === undefined ? undefined : findCountry(hovered);
  // Named on the way in and forgotten on the way out, so that leaving one country for the sea, for a shape filed
  // under no code at all, or for the next one along each say what they are.
  const naming = (code: string) => ({
    onMouseEnter: () => setHovered(code),
    onMouseLeave: () => setHovered(undefined),
  });

  // Every shape on the map is a country this page can show and so is the way to it, which is what makes the picture
  // worth clicking anywhere and not only along a border. Two of Natural Earth's points of view file a shape under no
  // ISO code at all, and those are land the way the sea is sea: they name nothing and go nowhere.
  const reaching = (code: string) =>
    findCountry(code) ? { ...naming(code), onClick: () => onSelect(pickCountry(code)), "data-reachable": true } : {};

  return (
    <Stack gap={6}>
      {drawn && (
        // Mantine's floating tooltip follows the pointer across the whole picture rather than sitting over one
        // shape, which is what a map wants: the shapes are two hundred paths and a country is whatever is under
        // the cursor. It composes with the handlers below rather than replacing them, and is off until one of
        // those has said which country that is — the sea names nothing, and neither does a shape with no code.
        <Tooltip.Floating label={under && <CountryName country={under} />} disabled={!under} position="top">
          <svg className="country-map" viewBox={VIEW_BOX} role="img" aria-label={mapLabel(country)}>
            {/* Everything drawn moves together, a flight being one picture panning and zooming and not a redraw. */}
            <g ref={flying} className="country-map-flight">
              {drawn.rest.map((shape) => (
                <path
                  key={shape.code}
                  className="country-map-land"
                  fillRule="evenodd"
                  d={shape.path}
                  {...reaching(shape.code)}
                />
              ))}
              {drawn.borders.map((shape) => (
                <path
                  key={shape.code}
                  className="country-map-neighbour"
                  fillRule="evenodd"
                  d={shape.path}
                  {...reaching(shape.code)}
                />
              ))}
              {drawn.own && (
                <path className="country-map-own" fillRule="evenodd" d={drawn.own} {...naming(country.cca2)} />
              )}
            </g>
          </svg>
        </Tooltip.Floating>
      )}
      <Text size="xs" c="dimmed">{viewText(boundaries)}</Text>
      {/* A country the view draws no boundary of its own for is not a page that broke, and says which one it is in. */}
      {country.cca2 in boundaries.absent && <Text size="xs" c="dimmed">{absentText(country, boundaries)}</Text>}
    </Stack>
  );
}

interface CountryMapProps {
  country: Country;
  view: string;
  onSelect: (country: Country) => void;
}

// Long enough to read as a movement and short enough not to be waited on: a country on the map is a link, and
// nobody clicking a link wants to watch it arrive.
const FLIGHT = 520;

// Off the mark and onto it, which is the whole of what is left for a curve to say here — the sampling in `flight`
// has already made the zoom itself even, and an easing over that is only the start and the stop.
const EASING = "ease-in-out";

// What the pointer is over, said the way the picker says it: the flag beside the name and never inside it.
function CountryName({ country }: { country: Country }) {
  return (
    <Group gap={6} wrap="nowrap">
      <Text span>{country.flag}</Text>
      <Text span size="sm">{country.name.common}</Text>
    </Group>
  );
}

// Whose boundaries these are, said in words under the map and not only as the value of a picker above it: the
// picker names a country and this is what naming it does. It is read off what was drawn rather than off what is
// selected, so a view still being fetched is a map captioned with the one it is still showing.
function viewText({ view }: Boundaries): string {
  const named = view !== DEFAULT_VIEW ? findCountry(view)?.name.common : undefined;
  return named
    ? `Boundaries as Natural Earth draws them for the ${named} point of view.`
    : "Boundaries as Natural Earth draws them by default, from the territory each country holds.";
}

// Which country a view draws over the land instead is only said where the point the list gives for the country is
// inside one, and that point is a rough middle rather than a place: Svalbard's is in a fjord between two of its own
// islands, so its map shows the archipelago drawn as part of Norway and this line stops at the half it is sure of.
function absentText(country: Country, { absent }: Boundaries): string {
  const holder = findCountry(absent[country.cca2])?.name.common;
  return holder
    ? `No boundary of its own in that view: this land is inside the shape filed under ${holder}.`
    : "No boundary of its own in that view.";
}

// Said only while Default is the one picked, that being the row in the list which names no country: every other
// one says whose view it is by being that country's name, and the first says nothing anybody can read off it.
const DEFAULT_MEANS = "Default draws each country as the territory it holds rather than as anybody claims it — "
  + "this information is provided by Natural Earth";

// Where to put the frame for a country the boundaries have none for, which is the point and the size the list
// carries. A square root turns an area into a width and a degree is a hundred and eleven kilometres of it, and
// then it is doubled: no country fills the square its area would make, and one that filled its own frame edge to
// edge would be a map of nowhere in particular. Svalbard is the one country the list gives an area of -1, which
// means nobody measured it rather than that it has none, so it is framed at the size of a small country.
function placeOf(country: Country): Place {
  const [latitude, longitude] = country.latlng;
  const across = country.area > 0 ? 2 * Math.sqrt(country.area) / KILOMETRES_TO_A_DEGREE : UNMEASURED;
  return { longitude, latitude, across };
}

const KILOMETRES_TO_A_DEGREE = 111;

const UNMEASURED = 6;

function mapLabel(country: Country): string {
  const borders = borderCountries(country).map((border) => border.name.common);
  return borders.length === 0
    ? `A map of ${country.name.common}, which has no land borders.`
    : `A map of ${country.name.common}, bordering ${borders.join(", ")}.`;
}

// Past this the codes are the area codes of a plan several countries share, and belong under a heading of their own.
const INLINE_PREFIXES = 4;

function inlinePrefixes(prefixes: string[]): string {
  return prefixes.length <= INLINE_PREFIXES ? prefixes.join(", ") : "";
}

// The flag is drawn beside the label and never inside it, per the note on COUNTRY_OPTIONS; the tick is Mantine's own,
// which a custom renderer is otherwise responsible for putting back. Both pickers are drawn by this, the point of
// view being a country in every case but the default one — which names no country and so is given no flag.
function renderCountryOption({ option, checked }: ComboboxLikeRenderOptionInput<{ value: string; label: string }>) {
  const flag = findCountry(option.value)?.flag;
  return (
    <Group gap="xs" wrap="nowrap">
      {checked && <CheckIcon size={12} />}
      {flag && <Text span>{flag}</Text>}
      <Text size="sm">{option.label}</Text>
    </Group>
  );
}

function Marker({ children }: { children: ReactNode }) {
  return <Badge variant="light" color="gray" size="sm" tt="none">{children}</Badge>;
}

function Section({ title, empty, children }: SectionProps) {
  return (
    <Box>
      <Title order={4} mb="xs">{title}</Title>
      {empty ? <Text size="sm">{empty}</Text> : children}
    </Box>
  );
}

interface SectionProps {
  title: string;
  // What to say instead of the table, or false when there is a table: a country with no currency of its own reads as
  // one that spends somebody else's rather than as a heading whose contents failed to arrive.
  empty?: string | false;
  children: ReactNode;
}

// Scrolled rather than squeezed below the width a name needs, and the cells wrap on their spaces alone: three
// columns crushed into a phone broke Deutschland across two lines, which is a name nobody can copy off the page.
function ColumnTable({ head, rows }: { head: string[]; rows: string[][] }) {
  return (
    <Table.ScrollContainer minWidth={360} type="native">
      <Table verticalSpacing={6} horizontalSpacing="xs" withRowBorders={false}>
        <Table.Thead>
          <Table.Tr>
            {head.map((column) => (
              <Table.Th key={column} fw={400}>
                <Text size="xs" c="dimmed">{column}</Text>
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr key={row.join(" ")}>
              {row.map((cell, column) => (
                <Table.Td key={head[column]}>
                  <Text size="sm">{cell}</Text>
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
